import Swiper, { Navigation } from 'swiper';

Swiper.use([Navigation]);

function setupItemGallery() {
  document.querySelectorAll('[data-wb-item-gallery]').forEach((gallery) => {
    if (gallery.getAttribute('data-wb-item-gallery-ready') === '1') {
      return;
    }

    const mainImage = gallery.querySelector('[data-wb-gallery-main]');
    const thumbsRail = gallery.querySelector('.wb-item-thumbs');
    const thumbs = Array.from(gallery.querySelectorAll('[data-wb-gallery-thumb]'));
    const dotsContainer = gallery.querySelector('.wb-item-photo__dots');
    const prev = gallery.querySelector('[data-wb-gallery-prev]');
    const next = gallery.querySelector('[data-wb-gallery-next]');
    let activeIndex = 0;
    let ignoreNextClick = false;

    if (!mainImage || !thumbs.length) {
      return;
    }

    gallery.setAttribute('data-wb-item-gallery-ready', '1');

    function normalizeIndex(index) {
      return (index + thumbs.length) % thumbs.length;
    }

    function getThumbSource(thumb) {
      const thumbImage = thumb.querySelector('img');

      return {
        src: thumb.getAttribute('data-image-src') || thumbImage?.currentSrc || thumbImage?.src || '',
        alt: thumb.getAttribute('data-image-alt') || thumbImage?.alt || '',
      };
    }

    function getScrollAxis() {
      if (!thumbsRail) {
        return 'y';
      }

      return thumbsRail.scrollWidth > thumbsRail.clientWidth + 2 ? 'x' : 'y';
    }

    function scrollRailTo(axis, value) {
      if (!thumbsRail) {
        return;
      }

      if (axis === 'x') {
        thumbsRail.scrollTo({ left: value, behavior: 'smooth' });
        return;
      }

      thumbsRail.scrollTo({ top: value, behavior: 'smooth' });
    }

    function ensureThumbVisible(index, direction = 0) {
      if (!thumbsRail || !thumbs[index]) {
        return;
      }

      const axis = getScrollAxis();
      const lookAheadIndex = normalizeIndex(index + direction);
      const target = direction !== 0 && thumbs[lookAheadIndex] ? thumbs[lookAheadIndex] : thumbs[index];
      const railSize = axis === 'x' ? thumbsRail.clientWidth : thumbsRail.clientHeight;
      const currentScroll = axis === 'x' ? thumbsRail.scrollLeft : thumbsRail.scrollTop;
      const targetStart = axis === 'x' ? target.offsetLeft : target.offsetTop;
      const targetSize = axis === 'x' ? target.offsetWidth : target.offsetHeight;
      const targetEnd = targetStart + targetSize;
      const railEnd = currentScroll + railSize;
      const scrollGap = 6;

      if (targetStart < currentScroll) {
        scrollRailTo(axis, Math.max(targetStart - scrollGap, 0));
        return;
      }

      if (targetEnd > railEnd) {
        scrollRailTo(axis, targetEnd - railSize + scrollGap);
      }
    }

    function createDots() {
      if (!dotsContainer) {
        return [];
      }

      dotsContainer.innerHTML = '';

      return thumbs.map((_, index) => {
        const dot = document.createElement('span');
        dot.className = 'wb-item-photo__dot';
        dot.setAttribute('data-wb-gallery-dot', '');
        dot.addEventListener('click', () => setActive(index));
        dotsContainer.append(dot);
        return dot;
      });
    }

    const dots = createDots();

    function setActive(index) {
      const normalizedIndex = normalizeIndex(index);
      const direction = index > activeIndex ? 1 : index < activeIndex ? -1 : 0;
      const thumb = thumbs[normalizedIndex];
      const { src, alt } = getThumbSource(thumb);

      if (!src) {
        return;
      }

      activeIndex = normalizedIndex;
      mainImage.src = src;
      mainImage.alt = alt;

      thumbs.forEach((item, itemIndex) => {
        item.classList.toggle('is-active', itemIndex === activeIndex);
        item.setAttribute('aria-selected', itemIndex === activeIndex ? 'true' : 'false');
      });

      dots.forEach((dot, dotIndex) => {
        dot.classList.toggle('is-active', dotIndex === activeIndex);
      });

      ensureThumbVisible(activeIndex, direction);
    }

    function activateThumb(thumb) {
      const index = thumbs.indexOf(thumb);

      if (index === -1) {
        return;
      }

      setActive(index);
    }

    function setupThumbRail() {
      if (!thumbsRail) {
        return;
      }

      let pointerId = null;
      let startX = 0;
      let startY = 0;
      let startScrollLeft = 0;
      let startScrollTop = 0;
      let startThumb = null;
      let isDragging = false;
      let hasPointerCapture = false;

      function finishPointer(event) {
        if (pointerId !== event.pointerId) {
          return;
        }

        const endThumb = event.target.closest?.('[data-wb-gallery-thumb]') || startThumb;
        const shouldActivate = !isDragging && endThumb && thumbsRail.contains(endThumb);

        if (hasPointerCapture) {
          thumbsRail.releasePointerCapture?.(pointerId);
        }

        thumbsRail.classList.remove('is-dragging');
        pointerId = null;
        startThumb = null;
        hasPointerCapture = false;

        if (shouldActivate) {
          ignoreNextClick = true;
          activateThumb(endThumb);
          window.setTimeout(() => {
            ignoreNextClick = false;
          }, 0);
        }
      }

      thumbsRail.addEventListener('pointerdown', (event) => {
        if (event.button !== undefined && event.button !== 0) {
          return;
        }

        pointerId = event.pointerId;
        startX = event.clientX;
        startY = event.clientY;
        startScrollLeft = thumbsRail.scrollLeft;
        startScrollTop = thumbsRail.scrollTop;
        startThumb = event.target.closest?.('[data-wb-gallery-thumb]') || null;
        isDragging = false;
        hasPointerCapture = false;
      });

      thumbsRail.addEventListener('pointermove', (event) => {
        if (pointerId !== event.pointerId) {
          return;
        }

        const deltaX = event.clientX - startX;
        const deltaY = event.clientY - startY;

        if (!isDragging && (Math.abs(deltaX) > 4 || Math.abs(deltaY) > 4)) {
          isDragging = true;
          thumbsRail.classList.add('is-dragging');
          thumbsRail.setPointerCapture?.(pointerId);
          hasPointerCapture = true;
        }

        if (!isDragging) {
          return;
        }

        event.preventDefault();
        thumbsRail.scrollLeft = startScrollLeft - deltaX;
        thumbsRail.scrollTop = startScrollTop - deltaY;
      });

      thumbsRail.addEventListener('pointerup', finishPointer);
      thumbsRail.addEventListener('pointercancel', finishPointer);
      thumbsRail.addEventListener('lostpointercapture', () => {
        thumbsRail.classList.remove('is-dragging');
      });

      thumbsRail.addEventListener('click', (event) => {
        const thumb = event.target.closest?.('[data-wb-gallery-thumb]');

        if (!thumb || !thumbsRail.contains(thumb)) {
          return;
        }

        event.preventDefault();

        if (ignoreNextClick) {
          ignoreNextClick = false;
          return;
        }

        activateThumb(thumb);
      });
    }

    prev?.addEventListener('click', () => setActive(activeIndex - 1));
    next?.addEventListener('click', () => setActive(activeIndex + 1));

    setupThumbRail();
    setActive(0);
  });
}

function setupQuantity() {
  document.querySelectorAll('[data-wb-qty]').forEach((qty) => {
    const valueNode = qty.querySelector('[data-wb-qty-value]');
    const minus = qty.querySelector('[data-wb-qty-minus]');
    const plus = qty.querySelector('[data-wb-qty-plus]');
    const min = Number(qty.getAttribute('data-min') || 1);
    const max = Number(qty.getAttribute('data-max') || 99);
    let value = Number(valueNode?.textContent || min);

    function render() {
      value = Math.min(Math.max(value, min), max);

      if (valueNode) {
        valueNode.textContent = String(value);
      }

      if (minus) {
        minus.disabled = value <= min;
      }

      if (plus) {
        plus.disabled = value >= max;
      }
    }

    minus?.addEventListener('click', () => {
      value -= 1;
      render();
    });

    plus?.addEventListener('click', () => {
      value += 1;
      render();
    });

    render();
  });
}

function markSwiperReady(el) {
  el.classList.add('wb-swiper-ready');
}

function setupRelatedSwiper() {
  document.querySelectorAll('.wb-related-swiper').forEach((el) => {
    if (el.swiper || el.classList.contains('wb-swiper-ready')) {
      return;
    }

    new Swiper(el, {
      slidesPerView: 1.25,
      slidesPerGroup: 1,
      spaceBetween: 16,
      speed: 500,
      watchOverflow: true,
      navigation: {
        prevEl: el.closest('.wb-related')?.querySelector('[data-wb-related-prev]'),
        nextEl: el.closest('.wb-related')?.querySelector('[data-wb-related-next]'),
      },
      breakpoints: {
        576: { slidesPerView: 2, spaceBetween: 18 },
        768: { slidesPerView: 3, spaceBetween: 20 },
        1200: { slidesPerView: 4, spaceBetween: 24 },
      },
      on: {
        init() {
          markSwiperReady(el);
        },
      },
    });

    markSwiperReady(el);
  });
}

function setupRelatedCardGalleries() {
  document.querySelectorAll('[data-wb-card-gallery]').forEach((gallery) => {
    if (gallery.getAttribute('data-wb-card-gallery-ready') === '1') {
      return;
    }

    const slides = Array.from(gallery.querySelectorAll(':scope > .swiper-wrapper > .swiper-slide'));
    const pagination = gallery.querySelector('.wb-related-card__pagination');
    let activeIndex = 0;

    if (!slides.length) {
      return;
    }

    gallery.setAttribute('data-wb-card-gallery-ready', '1');
    gallery.classList.add('wb-card-gallery-ready');

    if (pagination) {
      pagination.innerHTML = '';
    }

    const dots = slides.map((_, index) => {
      if (!pagination) {
        return null;
      }

      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'wb-related-card__dot';
      dot.setAttribute('aria-label', `Фото товара ${index + 1}`);

      dot.addEventListener('pointerdown', (event) => {
        event.stopPropagation();
      });

      dot.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        setActive(index);
      });

      pagination.append(dot);
      return dot;
    });

    function setActive(index) {
      activeIndex = (index + slides.length) % slides.length;

      slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === activeIndex;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });

      dots.forEach((dot, dotIndex) => {
        if (!dot) {
          return;
        }

        const isActive = dotIndex === activeIndex;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-current', isActive ? 'true' : 'false');
      });
    }

    setActive(0);
  });
}

function initProductItemPage() {
  setupItemGallery();
  setupQuantity();
  setupRelatedSwiper();
  setupRelatedCardGalleries();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProductItemPage, { once: true });
} else {
  initProductItemPage();
}
